import React, { useMemo } from "react";

import { Stack, Typography } from "@mui/material";
import EmptyCardDealWorkspace from "assets/images/EmptyCardDealWorkspace.png";
import { WorkspaceFolderCloseIcon } from "assets/material-custom-icons";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { FileIcon } from "components/old-data-box-components";
import { Routes } from "constants/routes";
import { useNavigate } from "hooks";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { IFolder } from "store/types";

import { EMPTY_CARD_CONTENT, EntityType } from "../../constants";
import { Breadcrumbs } from "./Metadata/Breadcrubms";
import { useWorkspace } from "./WorkspaceContext";

const tableBodyRowProps = {
  sx: {
    backgroundColor: "white",
    height: "32px",
    minHeight: "32px",
    "& td": { verticalAlign: "middle", height: "32px", minHeight: "32px" },
    "&:hover td": { backgroundColor: "#ecf1f8" },
    "&:last-child td": { borderBottom: "none" }
  }
};

export const Folders: React.FC = () => {
  const { globalFolders } = useWorkspace();
  const navigate = useNavigate();

  const columns = useMemo<MRT_ColumnDef<IFolder>[]>(
    () => [
      {
        id: "title",
        header: "Folder Name",
        accessorKey: "title",
        Cell: ({ cell }) => (
          <>
            <FileIcon type={EntityType.FOLDER} style={{ fontSize: 16, color: "#71899c", flexShrink: 0 }} />
            <Typography
              noWrap
              sx={{
                fontSize: 14,
                color: "#071f35",
                lineHeight: "16px",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {cell.getValue<string>()}
            </Typography>
          </>
        )
      }
    ],
    []
  );

  const table = useMaterialReactTable({
    data: globalFolders,
    columns,
    getRowId: (row) => String(row.id),
    enableFullScreenToggle: false,
    enableHiding: false,
    enableTopToolbar: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableStickyHeader: true,
    enableRowVirtualization: true,
    enableColumnActions: false,
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid #dbe8f5",
        borderRadius: "8px",
        overflow: "hidden",
        flex: 1,
        minHeight: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column"
      }
    },
    muiTableContainerProps: {
      sx: {
        flex: 1,
        minHeight: 0,
        overflow: "auto"
      }
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#f6f8fc",
        color: "#071f35",
        fontSize: 14,
        fontWeight: 500,
        height: 32,
        minHeight: 32,
        maxHeight: 32,
        py: "4px",
        px: "16px",
        borderBottom: "1px solid #dbe8f5"
      }
    },
    muiTableHeadRowProps: {
      sx: {
        height: "32px",
        minHeight: "32px",
        "& th": { height: "32px", minHeight: "32px" }
      }
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: "white",
        fontSize: 14,
        color: "#071f35",
        borderBottom: "1px solid #dbe8f5",
        height: 32,
        minHeight: 32,
        maxHeight: 32,
        py: "8px",
        px: "16px",
        verticalAlign: "middle",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden"
      }
    },
    muiTableBodyRowProps: ({ row }) => ({
      ...tableBodyRowProps,
      onClick: () => navigate({ route: "workspaceFolder", params: { folder_id: row.original.id } }),
      sx: { ...tableBodyRowProps.sx, cursor: "pointer" }
    })
  });

  return (
    <Stack
      direction="column"
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex"
      }}
      gap="16px"
    >
      <Stack direction="column" gap="16px" sx={{ flexShrink: 0 }}>
        <Breadcrumbs
          links={[
            { path: Routes.workspaceFolders, title: "Workspace" },
            { path: Routes.workspaceFolders, title: "Folders" }
          ]}
        />

        <Stack direction="row" alignItems="center" gap="10px" sx={{ width: "100%" }}>
          <Stack direction="row" alignItems="center" gap="10px" sx={{ flex: 1, minWidth: 0 }}>
            <WorkspaceFolderCloseIcon sx={{ fill: "#0A3142", fontSize: 20 }} />
            <Typography
              noWrap
              sx={{
                color: "#0a3142",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "24px"
              }}
            >
              Folders
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {globalFolders.length ? (
          <MaterialReactTable table={table} />
        ) : (
          <EmptyCard
            title={EMPTY_CARD_CONTENT.workspaceEmpty.title}
            description={{
              text: EMPTY_CARD_CONTENT.workspaceEmpty.description,
              externalText: EMPTY_CARD_CONTENT.workspaceEmpty.externalDescription,
              sx: { width: 388 }
            }}
            image={{
              src: EmptyCardDealWorkspace,
              sx: { width: 315, height: 140 }
            }}
          />
        )}
      </Stack>
    </Stack>
  );
};
